import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js'
import { Stripe, StripeEmbeddedCheckoutOptions } from '@stripe/stripe-js'
import { UUID } from 'crypto'

import {
  fetchCheckoutSession,
  createInvoiceSubscription,
  InvoicePaymentRequest,
} from '@/api/subscriptionsAPI'
import { fetchOrgAccountDetails, OrgAccountDetails } from '@/api/orgsAPI'
import PaymentMethodSelector, { PaymentMethod } from './PaymentMethodSelector'
import InvoicePaymentFormWithDiscount from './InvoicePaymentFormWithDiscount'
import TierSelector from './TierSelector'
import SubscriptionSelector, {
  SubscriptionFrequency,
} from './SubscriptionSelector'
import { toCanonicalFrequency } from '@/utils/frequency'
import { TIER_PRICING, getBillingAmount } from '@/utils/billingConstants'

type FlowStep =
  | 'tier_selection'
  | 'subscription_selection'
  | 'payment_method'
  | 'stripe_checkout'
  | 'invoice_form'

interface MembershipUpgradeProps {
  orgUUID: UUID | null
  orgName: string
  token: string | null
  stripePromise: Promise<Stripe | null>
  tier?: number
  onError: (error: string) => void
  onSuccess?: () => void
  userEmail?: string
  userFirstName?: string
  userLastName?: string
}

export default function MembershipUpgrade({
  orgUUID,
  orgName,
  token,
  stripePromise,
  tier,
  onError,
  onSuccess,
  userEmail,
  userFirstName,
  userLastName,
}: MembershipUpgradeProps) {
  const [currentStep, setCurrentStep] = useState<FlowStep>('tier_selection')
  const [selectedTier, setSelectedTier] = useState<number | null>(tier ?? null)
  const [selectedFrequency, setSelectedFrequency] =
    useState<SubscriptionFrequency>('annual')
  const [selectedMethod, setSelectedMethod] =
    useState<PaymentMethod>('credit_card')
  const [isLoading, setIsLoading] = useState(false)

  // Stripe-specific state
  const [checkoutOptions, setCheckoutOptions] =
    useState<StripeEmbeddedCheckoutOptions | null>(null)

  // Invoice-specific state
  const [existingAccountDetails, setExistingAccountDetails] =
    useState<OrgAccountDetails | null>(null)

  // Fetch existing account details on component mount
  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (!orgUUID || !token) return

      try {
        const result = await fetchOrgAccountDetails(token, orgUUID)
        if ('accountDetails' in result) {
          setExistingAccountDetails(result.accountDetails)
          // Pre-select billing frequency from existing account if available
          if (result.accountDetails?.billing_frequency) {
            setSelectedFrequency(
              result.accountDetails.billing_frequency as SubscriptionFrequency,
            )
          }
        }
      } catch (error) {
        console.error('Error fetching account details')
      }
    }

    fetchAccountDetails()
  }, [orgUUID, token])

  // The monthly + invoice combination is not offered. If the selection ever
  // lands there, snap the payment method back to credit card.
  useEffect(() => {
    if (selectedFrequency === 'monthly' && selectedMethod === 'invoice') {
      setSelectedMethod('credit_card')
    }
  }, [selectedFrequency, selectedMethod])

  // Compute billing disclosure shown above the Stripe CTA.
  // Amounts are ex-GST, matching the SubscriptionSelector display.
  const getBillingDisclosure = (): string | null => {
    if (selectedTier == null) return null
    const tierData = TIER_PRICING[selectedTier]
    if (!tierData) return null
    const monthly = tierData.monthly
    if (selectedFrequency === 'monthly') {
      return `You will be charged $${monthly.toFixed(2)} today. Billed monthly.`
    }
    if (selectedFrequency === 'quarterly') {
      const quarterly = getBillingAmount(monthly, 'quarterly')
      return `You will be charged $${quarterly.toFixed(2)} today for 3 months.`
    }
    if (selectedFrequency === 'annual') {
      const annual = getBillingAmount(monthly, 'annual')
      return `You will be charged $${annual.toFixed(2)} today for 12 months. (saves 20% vs monthly)`
    }
    return null
  }

  const handleTierSelected = () => {
    if (selectedTier != null) {
      setCurrentStep('subscription_selection')
    }
  }

  const handleFrequencySelect = (frequency: SubscriptionFrequency) => {
    setSelectedFrequency(frequency)
  }

  const handleSubscriptionContinue = () => {
    setCurrentStep('payment_method')
  }

  const handleBackToTierSelection = () => {
    setCurrentStep('tier_selection')
  }

  const handleBackToSubscriptionSelection = () => {
    setCurrentStep('subscription_selection')
  }

  const handleMethodChange = (method: PaymentMethod) => {
    setSelectedMethod(method)
  }

  const handlePaymentMethodContinue = async () => {
    if (selectedMethod === 'credit_card') {
      await handleStripeCheckout()
    } else {
      setCurrentStep('invoice_form')
    }
  }

  const handleStripeCheckout = useCallback(async () => {
    if (!orgUUID || !token || selectedTier == null) return

    try {
      setIsLoading(true)
      const canonicalFrequency = toCanonicalFrequency(selectedFrequency)
      const data = await fetchCheckoutSession(orgUUID, token, selectedTier, canonicalFrequency)

      setCheckoutOptions({
        clientSecret: data.clientSecret,
      })

      setCurrentStep('stripe_checkout')
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Error creating checkout session'
      onError(message)
    } finally {
      setIsLoading(false)
    }
  }, [orgUUID, token, onError, selectedTier, selectedFrequency])

  const handleBackToPaymentMethod = () => {
    setCheckoutOptions(null) // Clear stale session to force fresh fetch on retry
    setCurrentStep('payment_method')
  }

  const handleInvoiceSubmit = async (billingInfo: {
    billingLegalName: string
    billingEmail: string
    billingContactName: string
    billingContactPhone?: string
    billingAddressLine1: string
    billingAddressLine2?: string
    billingCity: string
    billingState?: string
    billingPostalCode: string
    billingCountry: string
    taxRegistrationNumber?: string
    discountCode?: string
  }) => {
    if (!orgUUID || !token || selectedTier == null) return

    try {
      setIsLoading(true)

      const invoiceData: InvoicePaymentRequest = {
        org_uuid: orgUUID,
        billing_legal_name: billingInfo.billingLegalName,
        billing_email: billingInfo.billingEmail,
        billing_contact_name: billingInfo.billingContactName,
        billing_contact_phone: billingInfo.billingContactPhone || undefined,
        billing_address_line1: billingInfo.billingAddressLine1,
        billing_address_line2: billingInfo.billingAddressLine2 || undefined,
        billing_city: billingInfo.billingCity,
        billing_state: billingInfo.billingState || undefined,
        billing_postal_code: billingInfo.billingPostalCode,
        billing_country: billingInfo.billingCountry,
        tax_registration_number: billingInfo.taxRegistrationNumber || undefined,
        billing_frequency: selectedFrequency,
        discount_code: billingInfo.discountCode || undefined,
        tier: selectedTier,
      }

      await createInvoiceSubscription(invoiceData, token)

      // redirect to success page for GTM tracking
      window.location.href = '/account/success'
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : 'Error setting up invoice billing',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'tier_selection':
        return (
          <Box>
            <TierSelector
              selectedTier={selectedTier}
              onTierSelect={setSelectedTier}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                disableRipple
                size="large"
                onClick={handleTierSelected}
                disabled={selectedTier == null}
                sx={{
                  minWidth: 200,
                  borderRadius: 3,
                  fontSize: '18px',
                  textTransform: 'none',
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )

      case 'subscription_selection':
        return (
          <Box>
            <SubscriptionSelector
              selectedFrequency={selectedFrequency}
              onFrequencySelect={handleFrequencySelect}
              tier={selectedTier ?? 4}
              hideMonthly={selectedMethod === 'invoice'}
            />
            <Box
              sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}
            >
              <Button
                variant="text"
                disableRipple
                onClick={handleBackToTierSelection}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disableRipple
                size="large"
                onClick={handleSubscriptionContinue}
                sx={{
                  minWidth: 200,
                  borderRadius: 3,
                  fontSize: '18px',
                  textTransform: 'none',
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )

      case 'payment_method':
        return (
          <Box>
            <PaymentMethodSelector
              selectedMethod={selectedMethod}
              onMethodChange={handleMethodChange}
              hideInvoice={selectedFrequency === 'monthly'}
            />
            {selectedMethod === 'credit_card' && getBillingDisclosure() && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 3 }}
                data-testid="billing-disclosure"
              >
                {getBillingDisclosure()}
              </Typography>
            )}
            <Box
              sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}
            >
              <Button
                variant="text"
                disableRipple
                onClick={handleBackToSubscriptionSelection}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disableRipple
                size="large"
                onClick={handlePaymentMethodContinue}
                disabled={isLoading}
                sx={{
                  minWidth: 200,
                  borderRadius: 3,
                  fontSize: '18px',
                  textTransform: 'none',
                }}
              >
                {isLoading ? 'Loading...' : 'Continue'}
              </Button>
            </Box>
          </Box>
        )

      case 'stripe_checkout':
        return (
          <Box>
            {checkoutOptions ? (
              <div id="checkout">
                <EmbeddedCheckoutProvider
                  key={checkoutOptions.clientSecret}
                  stripe={stripePromise}
                  options={checkoutOptions}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <Typography>Loading checkout...</Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                disableRipple
                onClick={handleBackToPaymentMethod}
                disabled={isLoading}
                sx={{
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Back to Payment Options
              </Button>
            </Box>
          </Box>
        )

      case 'invoice_form':
        return (
          <InvoicePaymentFormWithDiscount
            tier={selectedTier ?? undefined}
            billingFrequency={selectedFrequency}
            onSubmit={handleInvoiceSubmit}
            isLoading={isLoading}
            onBack={handleBackToPaymentMethod}
            orgName={orgName}
            userEmail={userEmail}
            userFirstName={userFirstName}
            userLastName={userLastName}
            existingAccountDetails={existingAccountDetails}
          />
        )

      default:
        return null
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {renderCurrentStep()}
    </Box>
  )
}
